import React from 'react';
import DisplayField from '../../../../../itrust_common/components/others/DisplayField';
import DateFormatView from '../../../../../itrust_common/components/others/DateFormatView';

const LENS_SPECIFIC_FIELDS = {
  sphere: ['sphere', 'curve', 'diameter'],
  toric: ['sphere', 'cylinder', 'axis', 'curve', 'diameter'],
  multifocal: ['sphere', 'add', 'curve', 'diameter'],
  multifocal_toric: ['sphere', 'cylinder', 'axis', 'add', 'curve', 'diameter'],
  ortho_k: ['material', 'lens_power', 'add', 'mf_type', 'curve', 'diameter', 'rzd1', 'rzd2', 'lza1', 'lza2', 'tint', 'dot'],
  hybrid: ['material', 'lens_power', 'add', 'mf_type', 'curve', 'diameter', 'skirt_radius', 'tint', 'dot'],
  scleral: ['material', 'lens_power', 'add', 'mf_type', 'curve', 'diameter', 'optical_zone', 'sag_dep', 'limbal', 'mid_peri', 'edge', 'tint', 'dot'],
  rgp: ['material', 'lens_power', 'add', 'mf_type', 'curve', 'diameter', 'optical_zone', 'tint', 'dot']
};

const ALL_LENS_FIELDS = [
  'sphere', 'curve', 'diameter', 'cylinder', 'axis', 'add', 'material', 'lens_power',
  'mf_type', 'tint', 'dot', 'skirt_radius', 'optical_zone', 'sag_dep', 'limbal',
  'mid_peri', 'edge', 'rzd1', 'rzd2', 'lza1', 'lza2'
];

const formatFieldLabel = (field) => {
  if (!field) return '';
  return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
};

const formatFieldValue = (value) => {
  if (value === true) return 'yes';
  if (value === false) return '';
  return value;
};

const getRequiredFields = (fieldsValues) => {
  if (!fieldsValues) return ['sphere', 'cylinder', 'axis', 'add', 'curve', 'diameter'];

  const normalizeLensType = (type) => (type ? String(type).trim().toLowerCase().replace(/ /g, '_') : null);

  const odLensType = normalizeLensType(fieldsValues.od_lens_type || fieldsValues.od_contact_lens_attributes?.lens_type);
  const osLensType = normalizeLensType(fieldsValues.os_lens_type || fieldsValues.os_contact_lens_attributes?.lens_type);

  const lensTypes = [odLensType, osLensType].filter(Boolean);

  let fields = [];
  if (lensTypes.length > 0) {
    lensTypes.forEach(type => {
      const specificFields = LENS_SPECIFIC_FIELDS[type];
      if (specificFields) {
        specificFields.forEach(f => {
          if (!fields.includes(f)) {
            fields.push(f);
          }
        });
      }
    });
  }

  // Also include any fields from ALL_LENS_FIELDS that have a non-empty value present
  ALL_LENS_FIELDS.forEach(field => {
    const odVal = fieldsValues[`od_${field}`];
    const osVal = fieldsValues[`os_${field}`];
    const hasValue = (odVal !== null && odVal !== undefined && odVal !== '') ||
                     (osVal !== null && osVal !== undefined && osVal !== '');
    if (hasValue && !fields.includes(field)) {
      fields.push(field);
    }
  });

  // Fallback to standard contact lens fields if empty
  if (fields.length === 0) {
    fields = ['sphere', 'cylinder', 'axis', 'add', 'curve', 'diameter'];
  }

  return fields;
};

let LensView = props => {
  const { fieldsValues, template } = props;

  if (!fieldsValues) return null;

  const requiredFields = getRequiredFields(fieldsValues);

  return (
    <div className="row">
      <div className="col-12">
        <div className="overview-table mb-3">
          <table className="table table-bordered bg-light table-hover shadow-none border border-muted">
            <thead className="text-dark border border-muted">
              <tr className="text-center">
                <th scope="col"></th>
                <th scope="col">
                  <DisplayField
                    isVisible={true}
                    label={"Lens Type"}
                  />
                </th>
                {requiredFields.map(field => (
                  <th scope="col" key={field}>
                    <DisplayField
                      isVisible={true}
                      label={formatFieldLabel(field)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["od", "os", "ou"].map(type => (
                <tr className="text-center" key={type}>
                  <td>
                    <h6>{type.toUpperCase()}</h6>
                  </td>
                  <td>
                    <DisplayField
                      isVisible={true}
                      value={fieldsValues[`${type}_contact_lens_brand_name`] || fieldsValues[`${type}_contact_lens_attributes`]?.brand_name}
                    />
                  </td>
                  {requiredFields.map(field => (
                    <td key={field}>
                      <DisplayField
                        isVisible={true}
                        value={formatFieldValue(fieldsValues[`${type}_${field}`])}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DisplayField
        isVisible={template?.fields?.issue_date?.visible !== false && template?.fields?.issue_date?.visible !== "false"}
        label={`${template?.fields?.issue_date?.custom_label || 'Issue Date'}:`}
        value={<DateFormatView date={fieldsValues.issue_date} />}
        className="col-2 py-4"
      />
      <DisplayField
        isVisible={template?.fields?.expiry_date?.visible !== false && template?.fields?.expiry_date?.visible !== "false"}
        label={`${template?.fields?.expiry_date?.custom_label || 'Expiry Date'}:`}
        value={<DateFormatView date={fieldsValues.expiry_date} />}
        className="col-2 py-4"
      />
      {fieldsValues.cleaning_solution && <DisplayField
        isVisible={template?.fields?.cleaning_solution?.visible !== false && template?.fields?.cleaning_solution?.visible !== "false"}
        label={`${template?.fields?.cleaning_solution?.custom_label || 'Cleaning Solution'}:`}
        value={fieldsValues.cleaning_solution}
        className="col-2 py-4"
      />}

      {fieldsValues.notes && <DisplayField
        isVisible={template?.fields?.notes?.visible !== false && template?.fields?.notes?.visible !== "false"}
        label={`${template?.fields?.notes?.custom_label || 'Comments'}:`}
        value={fieldsValues.notes}
        className="col-2 py-4"
      />}
    </div>
  );
};

export default LensView;